import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { FedexEtdImagesOutDTO } from '@api/ringil-api.v2';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import dayjs from 'dayjs';

/**
 * Company-level FedEx ETD letterhead + signature upload. FedEx prints these on
 * electronically generated commercial invoices; sizes are mandated exactly
 * (SIGNATURE 240x25 px, LETTERHEAD 700x50 px; PNG or GIF).
 */
const FedexEtdImagesSection: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { getFedexEtdImages, uploadFedexEtdImage, deleteFedexEtdImage } = useContext(ApiContext);

  const [state, setState] = useState<FedexEtdImagesOutDTO | null>(null);
  const [error, setError] = useState<{ message: string; params?: string[] } | null>(null);

  const errorCallbacks = {
    onValidationFailed: (response: any) => {
      const firstMessage = Array.isArray(response) ? response[0] : null;
      setError(firstMessage
        ? { message: firstMessage.message, params: firstMessage.params }
        : { message: 'FedEx error' });
    },
  };

  const load = () => getFedexEtdImages(data => setState(data));

  useEffect(() => {
    load();
  }, []);

  const handleFile = (imageType: 'SIGNATURE' | 'LETTERHEAD', file: File) => {
    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      uploadFedexEtdImage(
        imageType,
        { fileName: file.name, dataURL: reader.result as string },
        data => setState(data),
        null,
        errorCallbacks,
      );
    };
    reader.readAsDataURL(file);
  };

  if (state == null) return null;

  return (
    <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-content-quaternary">
      <COHeadline variant={'h4'} title={tr('CompanyProfileFedex.etd.title', 'Electronic trade documents (ETD)')} />
      <div className="text-sm text-content-secondary">
        {tr(
          'CompanyProfileFedex.etd.intro',
          'FedEx prints your signature and letterhead on electronically generated commercial invoices. Image sizes are mandated by FedEx exactly.',
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          <div className="font-medium">{tr(error.message, error.message)}</div>
          {error.params?.map((param, index) => (
            <div key={index} className="mt-1 text-xs">{param}</div>
          ))}
        </div>
      )}

      <EtdImageRow
        label={tr('CompanyProfileFedex.etd.signature', 'Signature (240 x 25 px, PNG/GIF)')}
        configured={state.signatureConfigured}
        uploadedAt={state.signatureUploadedAt}
        onFile={file => handleFile('SIGNATURE', file)}
        onDelete={() => deleteFedexEtdImage('SIGNATURE', data => setState(data))}
      />
      <EtdImageRow
        label={tr('CompanyProfileFedex.etd.letterhead', 'Letterhead (700 x 50 px, PNG/GIF)')}
        configured={state.letterheadConfigured}
        uploadedAt={state.letterheadUploadedAt}
        onFile={file => handleFile('LETTERHEAD', file)}
        onDelete={() => deleteFedexEtdImage('LETTERHEAD', data => setState(data))}
      />
    </div>
  );
};

interface EtdImageRowProps {
  label: string;
  configured: boolean;
  uploadedAt?: string;
  onFile: (file: File) => void;
  onDelete: () => void;
}

const EtdImageRow: React.FC<EtdImageRowProps> = ({ label, configured, uploadedAt, onFile, onDelete }) => {
  const { tr } = useTranslationLgs();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-row items-center gap-4">
      <div className="w-[320px]">
        <div className="text-sm font-medium text-content-primary">{label}</div>
        <div className="text-xs text-content-tertiary">
          {configured
            ? tr('CompanyProfileFedex.etd.uploaded', 'Uploaded {{date}}', { date: uploadedAt ? dayjs(uploadedAt).format('YYYY-MM-DD HH:mm') : '' })
            : tr('CompanyProfileFedex.etd.notUploaded', 'Not uploaded')}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/gif"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
      <RingilButton
        mode={'outlined-rectangle'}
        label={configured
          ? tr('CompanyProfileFedex.etd.replace', 'Replace')
          : tr('CompanyProfileFedex.etd.upload', 'Upload')}
        onClick={() => inputRef.current?.click()}
      />
      {configured && (
        <RingilButton
          mode={'outlined-rectangle'}
          label={tr('CompanyProfileFedex.etd.delete', 'Remove')}
          onClick={onDelete}
        />
      )}
    </div>
  );
};

export default FedexEtdImagesSection;
